import { useState } from "react";
import { Button } from "@/components/ui/button";
import { Skeleton } from "@/components/ui/skeleton";
import { SectionHeader } from "@/components/ui/section-header";
import { EmptyState } from "@/components/ui/empty-state";
import { IconDownload, IconLock, IconWifiOff } from "@tabler/icons-react";
import { CalibrationPhotoDrawer } from "@/components/calibration-photo-drawer";
import { CalibrationCard } from "@/components/calibration-card";
import type { NvpParameter } from "@/domain/calibration-params";
import { HiddenCalibrationRow } from "@/components/hidden-calibration-row";
import { ComparisonsSection } from "@/components/previous-events-section";
import { LiveStatus, useCalibrationWriteControls } from "@/components/calibration-live-controls";
import { usePhotoCategories } from "@/api/hooks";
import { flattenCategories } from "@/lib/photo-categories";
import { asCalibrationId } from "@/types/identifiers";
import type { BuildId, CalibrationId } from "@/types/identifiers";
import type { useCalibrationActions } from "@/hooks/use-calibration-actions";
import type { Action } from "@/providers/auth-provider";
import type { components } from "@/api/generated/schema";

type Calibration = components["schemas"]["CalibrationResponse"];

interface CalibrationsSectionProps {
  buildId: BuildId;
  calibrations: Calibration[] | undefined;
  isLoading: boolean;
  actions: ReturnType<typeof useCalibrationActions>;
  can: (action: Action) => boolean;
  isBoatOnline: boolean;
  readOnly?: boolean;
  eventName?: string;
  boatCode?: string | null;
}

export function CalibrationsSection({
  buildId,
  calibrations,
  isLoading,
  actions,
  can,
  isBoatOnline,
  readOnly = false,
  eventName,
  boatCode,
}: CalibrationsSectionProps) {
  const [photoDrawerCalId, setPhotoDrawerCalId] = useState<CalibrationId | null>(null);
  const [uploadingCalIds, setUploadingCalIds] = useState<Set<CalibrationId>>(new Set());
  const { data: allCategories } = usePhotoCategories();
  const calRoot = allCategories?.find((r) => r.name === "Calibration");
  const calCategoryId = flattenCategories(calRoot?.children ?? [])[0]?.id;
  const currentGolden = calibrations?.find((c) => c.state === "golden");
  const { controls, liveQuery, showLiveStatus } = useCalibrationWriteControls(buildId, {
    boatCode,
    boatOnline: isBoatOnline,
    canWrite: can("manage-calibrations"),
    currentGolden,
    eventName,
  });

  return (
    <>
      <div className="mt-6 flex items-center justify-between gap-4">
        <SectionHeader className="mb-0">Calibration History</SectionHeader>

        <div className="flex items-center gap-3">
          {showLiveStatus && <LiveStatus boatOnline={isBoatOnline} query={liveQuery} />}
          {!readOnly && can("manage-calibrations") && (
            <>
              {actions.hasSignedOff && (
                <span className="text-xs text-muted-foreground">Unlock first</span>
              )}
              {!isBoatOnline && !actions.hasSignedOff && (
                <span className="inline-flex items-center gap-1.5 text-xs px-2 py-1 rounded-md bg-amber-50 text-amber-700 dark:bg-amber-950 dark:text-amber-400">
                  <IconWifiOff className="h-3 w-3" />
                  Offline
                </span>
              )}
              <Button
                variant="outline"
                size="sm"
                className="text-xs h-7"
                onClick={actions.handleFetch}
                disabled={actions.isFetching || actions.hasSignedOff || !isBoatOnline}
              >
                {actions.isFetching
                  ? <span className="mr-1.5 h-3 w-3 border-2 border-muted-foreground/20 border-t-muted-foreground/60 rounded-full animate-spin" />
                  : actions.hasSignedOff
                    ? <IconLock className="mr-1.5 h-3 w-3" />
                    : !isBoatOnline
                      ? <IconWifiOff className="mr-1.5 h-3 w-3" />
                      : <IconDownload className="mr-1.5 h-3 w-3" />
                }
                {actions.isFetching ? "Fetching…" : "Fetch from NVP"}
              </Button>
            </>
          )}
        </div>
      </div>

      {isLoading ? (
        <div className="mt-4 space-y-2">
          {[...Array(3)].map((_, i) => (
            <Skeleton key={i} className="h-12 w-full rounded-lg" />
          ))}
        </div>
      ) : !calibrations || calibrations.length === 0 ? (
        <EmptyState message="No calibrations yet. Click &quot;Fetch from NVP&quot; to get started." className="mt-4" />
      ) : (
        <div className="mt-4 space-y-2">
          {actions.isFetching && (
            <div className="border-l-3 border-l-blue-400 rounded-r-lg bg-card pl-5 pr-5 py-4 space-y-2.5 animate-pulse">
              <div className="flex items-center gap-2">
                <Skeleton className="h-5 w-12 rounded" />
                <Skeleton className="h-5 w-20 rounded" />
              </div>
              <Skeleton className="h-3 w-48 rounded" />
              <Skeleton className="h-3 w-32 rounded" />
            </div>
          )}
          {actions.visibleCals.map((cal) => {
            const calId = asCalibrationId(cal.id);

            let compareProps: {
              compareParams?: NvpParameter[];
              compareLabel?: string;
              compareWarning?: string;
            } = {};

            if (cal.state === "golden") {
              const initials = actions.visibleCals.filter((c) => c.state === "initial");
              if (initials.length === 1) {
                compareProps = {
                  compareParams: initials[0].parameters as unknown as NvpParameter[],
                  compareLabel: "Initial",
                };
              } else if (initials.length > 1) {
                compareProps = {
                  compareWarning: `${initials.length} initial calibrations found — deselect extras to enable comparison`,
                };
              }
            }

            return (
              <CalibrationCard
                key={cal.id}
                cal={cal}
                actions={actions}
                can={can}
                isUploading={uploadingCalIds.has(calId)}
                onPhotoClick={() => setPhotoDrawerCalId(calId)}
                readOnly={readOnly}
                controls={controls}
                {...compareProps}
              />
            );
          })}
        </div>
      )}

      {actions.hiddenCals.length > 0 && (
        <div className="mt-6">
          <SectionHeader className="text-muted-foreground/50">
            Hidden ({actions.hiddenCals.length})
          </SectionHeader>
          <div className="space-y-1">
            {actions.hiddenCals.map((cal) => (
              <HiddenCalibrationRow
                key={cal.id}
                cal={cal}
                onUnhide={readOnly ? undefined : () => actions.handleToggleHidden(asCalibrationId(cal.id))}
                isPending={actions.isTogglingHidden}
              />
            ))}
          </div>
        </div>
      )}

      <ComparisonsSection
        buildId={buildId}
        eventName={eventName}
        currentGolden={currentGolden}
        controls={controls}
        initialParams={
          (() => {
            const initials = calibrations?.filter((c) => c.state === "initial");
            return initials?.length === 1
              ? (initials[0].parameters as unknown as NvpParameter[])
              : undefined;
          })()
        }
      />

      {photoDrawerCalId && calCategoryId && (
        <CalibrationPhotoDrawer
          open={!!photoDrawerCalId}
          onOpenChange={(open) => {
            if (!open) setPhotoDrawerCalId(null);
          }}
          buildId={buildId}
          calId={photoDrawerCalId}
          categoryId={calCategoryId}
          photo={calibrations?.find((c) => c.id === photoDrawerCalId)?.photo}
          onLoadingChange={(loading) => {
            setUploadingCalIds((prev) => {
              const next = new Set(prev);
              if (loading) next.add(photoDrawerCalId!);
              else next.delete(photoDrawerCalId!);
              return next;
            });
          }}
        />
      )}
    </>
  );
}
